import React from 'react';
import SbEditable, { SbEditableContent } from 'storyblok-react';
import { BlokComponent, NewStoryblokMetaData } from '../../types/blok.model';
import defaultTheme, { darkTheme } from '../../styles/themeVariables';
import RenderAfterPageTransition from '../../old/components/RenderAfterPageTransition';
import { ThemeProvider } from 'styled-components';
import { ThemeModeContextProvider } from '../../util/useThemeMode';
import Spacer from '../components/Spacer';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';
import { useContext } from 'react';
import { StoryblokMetaContext } from '../../old/storyblok/meta-context';
import { createArticleSchema } from '../../util/schemaHelpers';
import { useStoryblokLinks } from '../../util/useStoryblokLinks';
import { getBreadcrumbSchema } from '../../util/Breadcrumb/getBreadcrumbSchema';

export interface AiPageSchema extends NewStoryblokMetaData {
  body?: SbEditableContent[];
  bodyLower?: SbEditableContent[];
  bodySpacing?: string;
  bodyLowerSpacing?: string;
}

const AiPage: BlokComponent<AiPageSchema> = ({ blok, Dynamic }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            APP_NAME
          }
        }
      }
    `
  );

  const storyData = useContext(StoryblokMetaContext) as {
    date_published: string;
    slug: string;
    full_slug: string;
  };

  const articleSchema = createArticleSchema({
    title: blok?.meta_title,
    imageUrl: blok?.meta_image.filename,
    publishDate: storyData?.date_published,
    organizationName: site?.siteMetadata?.APP_NAME,
    organizationUrl: site?.siteMetadata?.siteUrl,
  });

  const links = useStoryblokLinks();

  const breadcrumbSchema = getBreadcrumbSchema(
    storyData,
    links,
    site.siteMetadata.siteUrl
  );

  return (
    <SbEditable content={blok}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <ThemeModeContextProvider themeMode={'light'}>
        <ThemeProvider theme={defaultTheme}>
          <RenderAfterPageTransition>
            {blok.body?.map((insideBlok, index) => (
              <Spacer
                key={insideBlok._uid}
                className={`item-${index} ${
                  insideBlok.spacing ?? blok.bodySpacing ?? 'default'
                } `}
              >
                <Dynamic blok={insideBlok} />
              </Spacer>
            ))}
          </RenderAfterPageTransition>
        </ThemeProvider>
      </ThemeModeContextProvider>
      <ThemeModeContextProvider themeMode={'dark'}>
        <ThemeProvider theme={darkTheme(defaultTheme)}>
          <RenderAfterPageTransition>
            {blok.bodyLower?.map((insideBlok, index) => (
              <Spacer
                key={insideBlok._uid}
                className={`item-${index} ${
                  insideBlok.spacing ?? blok.bodyLowerSpacing ?? 'default'
                } `}
              >
                <Dynamic blok={insideBlok} />
              </Spacer>
            ))}
          </RenderAfterPageTransition>
        </ThemeProvider>
      </ThemeModeContextProvider>
    </SbEditable>
  );
};

export default React.memo(AiPage);
